/* eslint no-eval: "off" */
import { FunctionComponent, useEffect, useState } from 'react';
import { Table, TableRow, TableCell, TableHeader, TableBody } from '@lmig/lmds-react-table';
import { Heading } from '@lmig/lmds-react-typography';
import { Modal, ModalBody, ModalHeader } from '@lmig/lmds-react-modal';
import { useSessionStorage } from 'Utilities/UseStorage';
import 'Feature/Alerts/Alert.scss';

const MoratoriumECatAlert: FunctionComponent<IMoratoriumECatAlertComponentData> = (props: any) => {
  const expirationMinutes = 5;
  const [alertMessageText, setAlertMessageText] = useState<string>();
  const [isOpen, setIsOpen] = useState(false);
  const sessionCacheKey = `MoratoriumECatAlertMoratoriumData-${props?.selectedstate}`;

  const [moratorium, setMoratoriumData] = useSessionStorage<IMoratoriumECatAlertMoratoriumData>(
    sessionCacheKey,
    undefined,
    expirationMinutes,
  );

  const sendRequest = async () => {
    let response: any = {};
    await fetch('/api/sitecore/PageContentAsync/GetMoratoriumECatAlerts', {
      method: 'POST',
      body: new URLSearchParams({ state: props?.selectedstate }),
    })
      .then((res) => res.json())
      .then(
        (res) => {
          try {
            response = JSON.parse(res);
          } catch (err: any) {
            console.error(`${'GetMoratoriumECatAlerts'}() >> ${err.message}`);
          }
        },
        (error) => {
          console.error(`${'GetMoratoriumECatAlerts'}() >> ${error}`);
        },
      );

    return response;
  };

  const getMoratoriumData = async () => {
    let apiResponse = await sendRequest();

    let moratoriumIsFullState = false;
    let moratoriumIsActive = false;

    if (
      apiResponse?.activeCountyCountAuto > 0 ||
      apiResponse?.activeCountyCountHome > 0 ||
      apiResponse?.activeZipCodeCountAuto > 0 ||
      apiResponse?.activeZipCodeCountHome > 0
    ) {
      moratoriumIsActive = true;

      if (
        apiResponse?.activeCountyCountAuto === apiResponse?.totalCountyCount ||
        apiResponse?.activeCountyCountHome === apiResponse?.totalCountyCount ||
        apiResponse?.activeZipCodeCountAuto === apiResponse?.totalCountyCount ||
        apiResponse?.activeZipCodeCountHome === apiResponse?.totalCountyCount
      ) {
        moratoriumIsFullState = true;
      }
    }

    setMoratoriumData({
      data: {
        moratoriumECatAlerts: apiResponse?.moratoriumEntityList ?? new Array({} as IMoratoriumECatAlert),
        moratoriumIsActive: moratoriumIsActive ?? false,
        moratoriumIsFullState: moratoriumIsFullState ?? false,
        activeCountyCountAuto: apiResponse?.activeCountyCountAuto ?? 0,
        activeZipCodeCountAuto: apiResponse?.activeZipCodeCountAuto ?? 0,
        activeCountyCountHome: apiResponse?.activeCountyCountHome ?? 0,
        activeZipCodeCountHome: apiResponse?.activeZipCodeCountHome ?? 0,
      } as IMoratoriumECatAlertMoratoriumData,
    });
  };

  const replaceTokens = () => {
    if (props?.alertmessagetext) {
      let tokenReplacedAlertMessageText = props?.alertmessagetext.replaceAll('{state}', props?.selectedstatefullname);
      let countiesAndZipCodesText: string[] = [];
      let maxActiveCounties = Math.max(moratorium.activeCountyCountAuto, moratorium.activeCountyCountHome);
      let maxActiveZipCodes = Math.max(moratorium.activeZipCodeCountAuto, moratorium.activeZipCodeCountHome);
      if (moratorium) {
        if (maxActiveCounties > 0) {
          let countyText = maxActiveCounties === 1 ? '1 county' : `${maxActiveCounties} counties`;
          countiesAndZipCodesText.push(countyText);
        }
        if (maxActiveZipCodes > 0) {
          let zipCodeText = maxActiveZipCodes === 1 ? '1 zip code' : `${maxActiveZipCodes} zip codes`;
          countiesAndZipCodesText.push(zipCodeText);
        }
        if (maxActiveCounties <= 1 && maxActiveZipCodes <= 1) {
          tokenReplacedAlertMessageText = tokenReplacedAlertMessageText.replaceAll(' are ', ' is ');
        }
      }
      tokenReplacedAlertMessageText = tokenReplacedAlertMessageText.replaceAll(
        '{countyAndZipCodes}',
        countiesAndZipCodesText.join(' and '),
      );
      setAlertMessageText(tokenReplacedAlertMessageText);
    }
  };

  useEffect(
    () => {
      if (!moratorium) {
        getMoratoriumData();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(
    () => {
      if (moratorium && moratorium?.moratoriumIsActive) {
        replaceTokens();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [moratorium],
  );

  return moratorium?.moratoriumIsActive && moratorium?.moratoriumECatAlerts ? (
    <>
      <div id="moratorium-ecat-alert" className="now-alert-wrapper">
        <div role="alert" className="sco--customer sco-notification sco-pageLevel sco--error">
          <p className="sco--customer sco-paragraph">
            <span className="embolden">{alertMessageText}</span>
            <span
              style={{
                color: '#06748c',
                cursor: 'pointer',
                textDecoration: 'underline',
                marginLeft: '5px',
                fontSize: 'unset',
              }}
              onClick={() => setIsOpen(true)}
            >
              {props?.modallinktext}
            </span>
          </p>
        </div>
      </div>

      <Modal
        isOpen={isOpen}
        size="large"
        id="moratorium-ecat-alert-modal"
        data-testid="moratorium-ecat-alert-modal-test"
        className="moratorium-ecat-alert-modal"
        onClose={() => setIsOpen(false)}
      >
        <ModalBody>
          <ModalHeader>{props?.modalheadertext.replaceAll('{state}', props?.selectedstatefullname)}</ModalHeader>
          {moratorium.moratoriumIsFullState ? (
            <>
              <Heading align="left" type="h4" style={{ marginLeft: '1rem' }}>
                Counties
              </Heading>
              <Table rowStyle="zebra">
                <TableHeader>
                  <TableRow>
                    <TableCell type="colHead">Affected County</TableCell>
                    <TableCell type="colHead">Line of Business</TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow key={1}>
                    <TableCell>All Counties</TableCell>
                    <TableCell>All Lines of Business</TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Heading align="left" type="h4" style={{ marginLeft: '1rem' }}>
                Zip Codes
              </Heading>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableCell type="colHead">Affected Zip Code</TableCell>
                    <TableCell type="colHead">Line of Business</TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow key={1}>
                    <TableCell>All Zip Codes</TableCell>
                    <TableCell>All Lines of Business</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </>
          ) : null}
          {!moratorium.moratoriumIsFullState &&
          (moratorium.activeCountyCountAuto > 0 || moratorium.activeCountyCountHome > 0) ? (
            <>
              <Heading align="left" type="h4" style={{ marginLeft: '1rem' }}>
                Counties
              </Heading>
              <Table rowStyle="zebra">
                <TableHeader>
                  <TableRow>
                    <TableCell type="colHead">Affected County</TableCell>
                    <TableCell type="colHead">Line of Business</TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {moratorium.moratoriumECatAlerts.map((m, index) =>
                    m.county ? (
                      <TableRow key={index}>
                        <TableCell>{m.county}</TableCell>
                        <TableCell>{m.lineOfBusiness}</TableCell>
                      </TableRow>
                    ) : null,
                  )}
                </TableBody>
              </Table>
            </>
          ) : null}
          {!moratorium.moratoriumIsFullState &&
          (moratorium.activeZipCodeCountAuto > 0 || moratorium.activeZipCodeCountHome > 0) ? (
            <>
              <Heading align="left" type="h4" style={{ marginLeft: '1rem' }}>
                Zip Codes
              </Heading>
              <Table rowStyle="zebra">
                <TableHeader>
                  <TableRow>
                    <TableCell type="colHead">Affected Zip Code</TableCell>
                    <TableCell type="colHead">Line of Business</TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {moratorium.moratoriumECatAlerts.map((m, index) =>
                    m.zipCode ? (
                      <TableRow key={index}>
                        <TableCell>{m.zipCode}</TableCell>
                        <TableCell>{m.lineOfBusiness}</TableCell>
                      </TableRow>
                    ) : null,
                  )}
                </TableBody>
              </Table>
            </>
          ) : null}
        </ModalBody>
      </Modal>
    </>
  ) : null;
};

export default MoratoriumECatAlert;
